import { useTranslation } from 'react-i18next';

import { ManifestLink } from '@remix-pwa/sw';
import {
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteLoaderData,
  useSearchParams,
} from '@remix-run/react';
import cx from 'classnames';

import { AppGlobalsScript } from '~/components/AppGlobals';
import FacebookPixel from '~/components/Pixels/FacebookPixel';
import GA4Pixel from '~/components/Pixels/GA4Pixel';
import GAPixel from '~/components/Pixels/GAPixel';
import { GTMPixel, GTMScript } from '~/components/Pixels/GTMPixel';
import TiktokPixel from '~/components/Pixels/TiktokPixel';
import TwitterPixel from '~/components/Pixels/TwitterPixel';
import { useDisplayMode } from '~/hooks/useDisplayMode';
import type { RootLoaderData } from '~/root';

import { ComponentErrorBoundary } from '../ComponentErrorBoundary';
import GSCVerification from '../Pixels/GSCVerification';
import HumanEmbed from '../Scripts/HumanEmbed';

// biome-ignore lint/suspicious/noExplicitAny: Keeping for backwards compatibility
const Document = ({ children, title, publication, isProdEnv, rootStyles, cookies }: any) => {
  const { noScript } = (useRouteLoaderData('root') ?? {}) as RootLoaderData;
  const location = useLocation();
  const { i18n } = useTranslation();
  const displayMode = useDisplayMode();
  // We use this to prevent scroll restoration on the iframe
  // When a user is editing we refresh the route and don't want it to scroll back to the top if they are making edits in the DesignLab
  // Remove this will have side effects in the experience of editing in the DesignLab
  const [searchParams] = useSearchParams();
  const isPreviewingFromSwarmIframe = searchParams.get('version') === 'draft';
  const preventScrollOnFilter = searchParams.has('tags') || (searchParams.has('q') && location.pathname === '/');

  const isSubscribePage = location.pathname === '/subscribe';
  const isHomePage = location.pathname === '/';
  const isPostPage = location.pathname.startsWith('/p/');
  const isCustomPage = location.pathname.startsWith('/c/');
  const isAuthorPage = location.pathname.startsWith('/authors');
  const isTagPage = location.pathname.startsWith('/t/');
  const isTestHomePage = location.pathname === '/test_home_page';

  // Compliance
  const cookiesBanner = publication?.enable_cookie_banner;
  const cookiesLocalStorage = cookies?.beehiiv_honey;

  let marketingCookies = !cookiesBanner;
  let dataProcessorCookies = !cookiesBanner;

  if (cookiesBanner) {
    marketingCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.marketing || false
        : !(cookiesBanner && !cookiesLocalStorage);
    dataProcessorCookies =
      cookiesBanner && cookiesLocalStorage
        ? JSON.parse(cookiesLocalStorage)?.dataProcessing || false
        : !(cookiesBanner && !cookiesLocalStorage);
  }

  // Pixels
  const facebookPixel = publication?.facebook_pixel;
  const twitterPixel = publication?.twitter_pixel;
  const tiktokPixel = publication?.tiktok_pixel;
  const gaPixel = publication?.google_analytics_pixel;
  const ga4Pixel = publication?.google_analytics_4_pixel;
  const gtmPixel = publication?.gtm_pixel;
  const gscCode = publication?.gsc_verification_code;

  let bodyClass = '';

  if (isSubscribePage) {
    bodyClass = 'bg-wt-subscribe_background text-wt-text-on-subscribe_background';
  }

  if (isHomePage || isPostPage || isCustomPage || isAuthorPage || isTagPage) {
    bodyClass = 'bg-wt-background text-wt-text-on-background';
  }

  const viewport = `width=device-width, initial-scale=1, shrink-to-fit=no${displayMode === 'standalone' ? ', user-scalable=no' : ''}`;

  return (
    <html lang={i18n.language} className="h-full antialiased">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content={viewport} />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="theme-color" content="#000000" />
        <AppGlobalsScript />
        <HumanEmbed />
        {title ? <title>{title}</title> : null}
        {publication?.web_template?.pwa_enabled && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: `
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__DeferredPrompt__ = e;
});
          `,
              }}
            />
            <ManifestLink />
          </>
        )}
        <Meta />
        <style
          suppressHydrationWarning={true}
          dangerouslySetInnerHTML={{
            __html: rootStyles,
          }}
        />
        <link rel="apple-touch-icon" href={publication?.logo?.thumb?.url} />
        <link rel="apple-touch-startup-image" href={publication?.thumbnail?.url} />
        <Links />
        {isProdEnv && (
          <ComponentErrorBoundary
            fallbackRender={() => {
              return null;
            }}
          >
            <GSCVerification code={gscCode} />
            <GTMScript dataProcessingCookies={dataProcessorCookies} pixelId={gtmPixel} />
            <FacebookPixel marketingCookies={marketingCookies} pixelId={facebookPixel} />
            <TwitterPixel marketingCookies={marketingCookies} pixelId={twitterPixel} />
            <TiktokPixel marketingCookies={marketingCookies} pixelId={tiktokPixel} />
            <GA4Pixel marketingCookies={marketingCookies} pixelId={ga4Pixel} />
            <GAPixel marketingCookies={marketingCookies} pixelId={gaPixel} />
          </ComponentErrorBoundary>
        )}
        {isCustomPage && <script type="text/javascript" async src="https://embeds.beehiiv.com/attribution.js" />}
      </head>
      <body suppressHydrationWarning className={cx('flex h-full flex-col', bodyClass)}>
        {isProdEnv && (
          <ComponentErrorBoundary
            fallbackRender={() => {
              return null;
            }}
          >
            <GTMPixel dataProcessingCookies={dataProcessorCookies} pixelId={gtmPixel} />
          </ComponentErrorBoundary>
        )}
        {/** Prevent any user clicks when previewing so the user doesn't navigate to the wrong page in the iframe */}
        <div className={cx(isPreviewingFromSwarmIframe && !isTestHomePage && 'pointer-events-none')}>{children}</div>
        {!preventScrollOnFilter && !isPreviewingFromSwarmIframe && <ScrollRestoration />}
        {!noScript && <Scripts />}
      </body>
    </html>
  );
};

export default Document;
